<template>
  <div class="w-full flex justify-center gap-2">
    <UseExample :src="exampleSrc" />
    <div class="glass-frosted relative rounded-8xl w-250 h-67.5 p-4 min-h-fit">
      <div class="inline-block">
        <div class="w-full h-full flex gap-6">
          <div class="w-110 h-full shrink-0 pl-2">
            <div>
              <p class="text-s-medium text-black-white mb-2">
                {{ $t('keys.config.quickSwitchConfig') }}
              </p>
              <div class="flex gap-1.5 mb-2">
                <Button
                  v-for="switchConfig in switchConfigs"
                  :key="switchConfig.code"
                  color="normal"
                  class="h-10 pr-3"
                  draggable="true"
                  @click="() => handleSelectConfig(switchConfig)"
                  @dragstart="(event: DragEvent) => handleDragStartConfig(switchConfig, event)"
                >
                  <Icon :icon="switchConfig.icon" class="size-6!" />
                  <span class="text-m-medium">{{ $t(switchConfig.labelKey) }}</span>
                </Button>
              </div>
              <p class="text-s-medium text-black-white mb-2">
                {{ $t('keys.config.quickConfigFile') }}
              </p>
              <div class="flex gap-4 mb-3 w-full">
                <div
                  v-for="(config, index) in configs"
                  :key="index"
                  class="w-24.5 h-24.5 relative flex justify-center items-center config-box"
                  :draggable="config.confIndex !== 99"
                  :class="`${config.keywords === '' ? 'cursor-not-allowed' : 'cursor-pointer'} ${config.confIndex !== 99 && 'config-box--active'}`"
                  @click="() => handleSelectConfig(config)"
                  @dragstart="(event: DragEvent) => handleDragStartConfig(config, event)"
                >
                  <div class="flex flex-col items-center">
                    <p class="text-m-medium h-5.5 w-20 cursor-default truncate text-center">
                      {{ config.displayName }}
                    </p>
                    <div
                      :class="[
                        'flex items-center mt-1.75',
                        !config.code && 'text-text-body-default',
                      ]"
                    >
                      <Icon :icon="config.icon" class="w-6 h-6" />
                    </div>
                  </div>
                </div>
              </div>
              <div class="text-text-glass-default flex items-start">
                <Icon icon="Function-FiII-Description" class="w-4 h-4 shrink-0 mr-0.5 mt-0.5" />
                <span class="text-s-medium">
                  {{ $t('keys.config.conflictTip') }}
                </span>
              </div>
            </div>
          </div>
          <div class="w-full p-4 flex flex-col items-center justify-between rounded-4xl bg-key">
            <div
              class="my-2 flex flex-col justify-center items-center"
              :style="{ width: '374px', height: '125px' }"
            >
              <div class="flex items-center gap-3">
                <Button v-for="value in ['A', 'B', 'C', 'D']" :key="value" color="glass" size="xs">
                  <Icon icon="Status-Fill-Configuration" class="w-3.5 h-3.5" />
                  <span class="text-s-medium text-black-white">
                    {{ $t('keys.config.profile') }} {{ value }}
                  </span>
                </Button>
              </div>
              <svg
                className="absolute top-9 left-0 w-full h-8 pointer-events-none"
                viewBox="0 0 374 32"
                fill="none"
              >
                <g strokeWidth="1.2" class="stroke-black-white">
                  <path d="M 187 32 V 26" />

                  <path d="M 187 26 Q 187 20, 167 20 L 105 20 Q 85 20, 85 8" />

                  <path d="M 187 26 Q 187 20, 172 20 L 163 20 Q 153 20, 153 8" />

                  <path d="M 187 26 Q 187 20, 202 20 L 211 20 Q 221 20, 221 8" />

                  <path d="M 187 26 Q 187 20, 207 20 L 269 20 Q 289 20, 289 8" />
                </g>

                <g class="fill-black-white">
                  <path d="M 85 4 L 82 9 H 88 Z" />
                  <path d="M 153 4 L 150 9 H 156 Z" />
                  <path d="M 221 4 L 218 9 H 224 Z" />
                  <path d="M 289 4 L 286 9 H 292 Z" />
                </g>
              </svg>
              <div class="flex items-center gap-3">
                <div
                  class="flex justify-center items-center w-10 h-10 rounded-md bg-key text-s-medium text-black-white"
                >
                  Esc
                </div>
                <div
                  class="flex items-center gap-2 px-3 py-2.5 rounded-2xl border border-black-white"
                >
                  <div
                    v-for="value in ['F1', 'F2', 'F3', 'F4']"
                    :key="value"
                    class="relative flex justify-center items-center w-10 h-10 rounded-md bg-green-default"
                  >
                    <Icon
                      icon="Hardware-Wireframe-proflie1"
                      class="w-6 h-6 relative top-1.5 text-neutral-dark-active"
                    />
                    <span class="absolute top-0 right-px text-s-medium text-text-body-default">
                      {{ value }}
                    </span>
                  </div>
                </div>
                <div
                  class="flex justify-center items-center w-10 h-10 rounded-md bg-key text-s-medium text-black-white"
                >
                  F5
                </div>
              </div>
              <!-- <img :src="configTip" class="max-w-93.5" /> -->
            </div>
            <div class="text-text-glass-default flex items-start">
              <Icon icon="Function-FiII-Description" class="w-4 h-4 shrink-0 mr-0.5 mt-0.5" />
              <span class="text-s-medium">
                {{ $t('keys.config.suggestionTip') }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, inject, ref, watch } from 'vue'
  import { storeToRefs } from 'pinia'
  import { Config } from '@rapoo/device-sdk'
  import { Button, Icon, toast } from '@rapoo/element'
  import { useConfigName } from '@/composables/useConfigName'
  import { useConfigurationStore, useDeviceStore, useLocaleStore } from '@/stores'
  import { setDragData, DragType } from '@/utils/dnd'
  import { useDragImage } from '../composables/useDragImage'
  import UseExample from '@/components/UseExample.vue'
  import { KEYCODE_KEYWORDS_MAP, CONFIG_KEY_ITEMS } from '../utils/keyMapSettings'
  import { SelectedKeySymbol, UpdateSelectedKeyMappingSymbol } from '../types'
  import useExampleZhCN from '@/assets/images/recording/keyboard/config-zhCN.gif'
  import useExampleEnUS from '@/assets/images/recording/keyboard/config-enUS.gif'

  import type { KeyChangePayload, UpdateSelectedKeyMapping } from '../types'

  interface ConfigKey {
    labelKey: string
    displayName: string
    keywords: string
    confIndex?: number // 0, 1, 2, 3  99表示没有配置
    icon: string
    [key: string]: any
  }

  const selectedKey = inject(SelectedKeySymbol)
  const updateSelectedKeyMapping = inject<UpdateSelectedKeyMapping>(UpdateSelectedKeyMappingSymbol)

  const { language } = useLocaleStore()

  const exampleSrc = computed(() => (language === 'zh-CN' ? useExampleZhCN : useExampleEnUS))

  const switchConfigs = computed<ConfigKey[]>(() => [
    {
      labelKey: 'keys.config.prevConfig',
      displayName: '',
      keywords: 'prev config',
      type: '9',
      code: '0x28',
      icon: 'Hardware-Wireframe-PrevConfig',
    },
    {
      labelKey: 'keys.config.nextConfig',
      displayName: '',
      keywords: 'next config',
      type: '9',
      code: '0x27',
      icon: 'Hardware-Wireframe-NextConfig',
    },
  ])

  const configurationStore = useConfigurationStore()
  const deviceStore = useDeviceStore()
  const { currentDevice } = storeToRefs(deviceStore)
  const { configs: globalConfigs } = storeToRefs(configurationStore)
  const configs = ref<ConfigKey[]>()
  const getProfileIcon = (index: number) => `Hardware-Wireframe-proflie${index + 1}`

  watch(
    globalConfigs,
    (list) => {
      let result: Config[] = list.slice(0, 4)
      // 配置列表长度不足4，补齐到最大长度
      if (result.length < 4) {
        result = [...result, ...Array(4 - result.length).fill({} as Config)]
      }
      configs.value = result.map((item, index) => ({
        keywords: 'config' + index,
        labelKey: item?.name ?? '',
        displayName: useConfigName(item as Config, currentDevice.value),
        type: '9',
        code: '0x24',
        icon: getProfileIcon(index),
        confIndex: item?.index ?? 99,
      }))
    },
    { immediate: true },
  )

  // 配置选择
  const handleSelectConfig = (key: ConfigKey) => {
    if (selectedKey && selectedKey.value && updateSelectedKeyMapping) {
      let target
      target = KEYCODE_KEYWORDS_MAP.get(key.keywords)
      if (!target) {
        target = CONFIG_KEY_ITEMS.find((item) => item.keywords === key.keywords)
      }

      const payload: KeyChangePayload = {
        keywords: selectedKey.value.keywords,
        keyType: 'configuration',
        target: target!,
        raw: {
          ...target,
          icon: key.icon,
        },
      }

      updateSelectedKeyMapping(payload)
      selectedKey.value = null
      toast.dismiss('keyboard')
    }
  }

  const { setDragImage } = useDragImage()

  const handleDragStartConfig = (key: ConfigKey, event: DragEvent) => {
    const dataTransfer = event.dataTransfer
    if (!dataTransfer) return

    let target
    target = KEYCODE_KEYWORDS_MAP.get(key.keywords)
    if (!target) {
      target = CONFIG_KEY_ITEMS.find((item) => item.keywords === key.keywords)
    }

    const dragData: KeyChangePayload = {
      keywords: '',
      keyType: 'configuration',
      target: target!,
      raw: {
        ...target,
        icon: key.icon,
      },
    }

    setDragData(dataTransfer, DragType.KEY_MAPPING, dragData)
    setDragImage(event, {
      icon: key.icon,
    })
  }
</script>

<style scoped>
  .config-box {
    position: relative;
    overflow: hidden;

    /* default 背景 */
    background-image: url('../../../../../assets/images/devices/keys/config-container-default.png');
    background-size: cover;
    background-position: center;
  }

  /* hover 层 */
  .config-box::before {
    content: '';
    position: absolute;
    inset: 0;

    background-image: url('../../../../../assets/images/devices/keys/config-container-hover.png');
    background-size: cover;
    background-position: center;

    opacity: 0;
    transition: opacity 0.25s ease;

    pointer-events: none;
  }

  /* active 层 */
  .config-box::after {
    content: '';
    position: absolute;
    inset: 0;

    background-image: url('../../../../../assets/images/devices/keys/config-container-active.png');
    background-size: cover;
    background-position: center;

    opacity: 0;
    transition: opacity 0.25s ease;

    pointer-events: none;
  }

  /* hover 状态 */
  .config-box:hover::before {
    opacity: 1;
  }

  /* active 状态 */
  .config-box--active::after {
    opacity: 1;
  }

  /* active 时 hover */
  .config-box--active:hover::before {
    opacity: 1;
  }
  .config-box--active:hover::after {
    opacity: 0;
  }

  /* 内容置顶 */
  .config-box > * {
    position: relative;
    z-index: 1;
  }
</style>
